import React from 'react';
import { View, StyleSheet, ScrollView, Platform } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { AuthenticatedScreenParams } from '../../navigation/authenticated/authenticated.types';
import { Layout, Paragraph, Title, HomeScreenHeader } from '../../components';
import { useLanguage } from '../../locale';
import { useGetCompletedSurveys } from '../../hooks/api/survey.api';
import useUserData from '../../hooks/useUserData';
import useFeatureFlags from '../../hooks/useFeatureFlags';
import { SurveyCard } from '../feedback/components/SurveyCard';
import { SURVEY_DETAIL_SCREEN } from '../../navigation/authenticated/authenticated.types';
import { useBackNavbarAction } from '../../hooks/useBackNavbarAction';
import { s } from 'react-native-wind';

export const CompletedSurveysScreen: React.FC = () => {
  const [t] = useLanguage();
  const navigation = useNavigation<NativeStackNavigationProp<AuthenticatedScreenParams>>();
  const { company } = useUserData();
  const [FeatureFlags] = useFeatureFlags();
  const backAction = useBackNavbarAction();

  // Redirect back if the PulseSurvey feature flag is disabled
  React.useEffect(() => {
    if (!FeatureFlags.PulseSurvey) {
      navigation.goBack();
    }
  }, [FeatureFlags.PulseSurvey, navigation]);

  // If feature flag is disabled, don't render anything while redirecting
  if (!FeatureFlags.PulseSurvey) {
    return null;
  }

  const { data: completedSurveys, isLoading } = useGetCompletedSurveys(company?.id);

  const customHeader = React.useMemo(() => {
    return (
      <HomeScreenHeader
        horizontalPadding={false}
        title={t('survey.completedSurveys')}
      />
    );
  }, [t]);

  return (
    <Layout
      containerStyle={s`py-0`}
      isScrollView={Platform.OS === 'web'}
      showNavbar={true}
      leftAction={backAction}
    >
      {customHeader}
      <ScrollView
        style={styles.container}
        contentContainerStyle={styles.contentContainer}
      >

        {isLoading ? (
          <View style={styles.loadingContainer}>
            <Paragraph>{t('survey.loading')}</Paragraph>
          </View>
        ) : completedSurveys?.items && completedSurveys.items.length > 0 ? (
          <View style={s`mt-4`}>
            {completedSurveys.items.map((survey, index) => (
              <View key={survey.surveyId} style={index > 0 ? s`mt-4` : undefined}>
                <SurveyCard
                  survey={survey}
                  onCardPress={() => {
                    navigation.navigate(SURVEY_DETAIL_SCREEN, {
                      surveyId: survey.surveyId,
                    });
                  }}
                />
              </View>
            ))}
          </View>
        ) : (
          <View style={styles.emptyContainer}>
            <Paragraph>{t('survey.noCompletedSurveys')}</Paragraph>
          </View>
        )}
      </ScrollView>
    </Layout>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  contentContainer: {
    paddingBottom: 32,
  },
  loadingContainer: {
    marginTop: 24,
    alignItems: 'center',
  },
  emptyContainer: {
    marginTop: 24,
    alignItems: 'center',
  },
});
